import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import {
  FaChartBar,
  FaClock,
  FaFileExcel,
  FaFilePdf,
  FaFolderOpen,
  FaHourglassHalf,
  FaProjectDiagram,
  FaSyncAlt,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import api from "../../api";

const metricDetails = {
  totalStudents: { label: "Students", icon: FaUsers, tone: "#1b3a87", background: "#edf2ff" },
  totalProjects: { label: "Projects", icon: FaProjectDiagram, tone: "#6f42c1", background: "#f4efff" },
  activeVolunteers: { label: "Active volunteers", icon: FaUserCheck, tone: "#168475", background: "#e9f8f5" },
  approvedHours: { label: "Approved hours", icon: FaClock, tone: "#087e8b", background: "#e8f7f8" },
  pendingHours: { label: "Pending hours", icon: FaHourglassHalf, tone: "#b26a00", background: "#fff5df" },
  pendingProjects: { label: "Pending projects", icon: FaFolderOpen, tone: "#bd3f32", background: "#fff0ed" },
};

const AdminReports = () => {
  const [filters, setFilters] = useState({ startDate: "", endDate: "", status: "all" });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [error, setError] = useState("");

  const queryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate) params.set("endDate", filters.endDate);
    if (filters.status !== "all") params.set("status", filters.status);
    return params;
  }, [filters]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const summaryResponse = await api.get(`/reports/summary?${queryParams().toString()}`);
      setSummary(summaryResponse.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Unable to load report data.");
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const download = async (format) => {
    setDownloading(format);
    setError("");
    try {
      const response = await api.get(
        `/reports/export/${format}?${queryParams().toString()}`,
        { responseType: "blob" }
      );
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `wcco-admin-report.${format === "excel" ? "xlsx" : "pdf"}`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (requestError) {
      setError(requestError.response?.data?.error || `Unable to create the ${format} report.`);
    } finally {
      setDownloading("");
    }
  };

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const metrics = summary
    ? Object.entries(metricDetails).map(([key, details]) => ({ key, ...details, value: summary.metrics[key] }))
    : [];

  const maxCategoryHours = summary
    ? Math.max(0, ...summary.distributions.categories.map((category) => Number(category.approvedHours) || 0))
    : 0;

  return (
    <div className="d-flex flex-column flex-lg-row" style={{ maxWidth: "100vw", minHeight: "100vh", overflowX: "hidden" }}>
      <Sidebar />
      <Container
        fluid
        className="py-3 px-3 px-md-4"
        style={{
          height: "100vh",
          backgroundColor: "var(--color-bg)",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
          <TopNavBar />

          <Card
            className="border-0 p-4 mt-3 mb-4 text-white overflow-hidden"
            style={{
              background: "linear-gradient(135deg, var(--color-primary) 0%, #3158b5 100%)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "0 12px 30px rgba(27, 58, 135, 0.18)",
            }}
          >
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
              <div>
                <div className="small fw-bold text-uppercase mb-2" style={{ letterSpacing: "0.12em", opacity: 0.72 }}>
                  Admin analytics
                </div>
                <h2 className="fw-bold mb-2">Reports & Data Export</h2>
                <p className="mb-0 text-white-50">Review programme performance, then export the same filtered data.</p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button className="px-3" variant="light" onClick={() => download("pdf")} disabled={Boolean(downloading) || loading}>
                  {downloading === "pdf" ? <Spinner size="sm" className="me-2" /> : <FaFilePdf className="me-2" />}
                  PDF Report
                </Button>
                <Button className="px-3" style={{ backgroundColor: "var(--color-teal)", borderColor: "var(--color-teal)" }} onClick={() => download("excel")} disabled={Boolean(downloading) || loading}>
                  {downloading === "excel" ? <Spinner size="sm" className="me-2" /> : <FaFileExcel className="me-2" />}
                  Excel Export
                </Button>
              </div>
            </div>
          </Card>

          {error && <Alert variant="danger">{error}</Alert>}

          <Card className="p-4 mb-4 shadow-sm" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
              <div>
                <h5 className="fw-bold mb-1">Report filters</h5>
                <div className="small text-muted">Date ranges apply to hour-log activity; inventory totals remain complete.</div>
              </div>
              <FaChartBar size={22} style={{ color: "var(--color-primary)", opacity: 0.8 }} />
            </div>
            <Form>
              <Row className="align-items-end g-3">
                <Col md={6} xl={3}>
                  <Form.Label className="small fw-semibold">Activity start date</Form.Label>
                  <Form.Control type="date" name="startDate" value={filters.startDate} max={filters.endDate || undefined} onChange={updateFilter} />
                </Col>
                <Col md={6} xl={3}>
                  <Form.Label className="small fw-semibold">Activity end date</Form.Label>
                  <Form.Control type="date" name="endDate" value={filters.endDate} min={filters.startDate || undefined} onChange={updateFilter} />
                </Col>
                <Col md={6} xl={3}>
                  <Form.Label className="small fw-semibold">Hour-log status</Form.Label>
                  <Form.Select name="status" value={filters.status} onChange={updateFilter}>
                    <option value="all">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </Form.Select>
                </Col>
                <Col md={6} xl={3}>
                  <Button className="w-100" style={{ minHeight: 38 }} onClick={loadSummary} disabled={loading}>
                    <FaSyncAlt className={`me-2${loading ? " fa-spin" : ""}`} /> Apply filters
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : summary && (
            <>
              <Row className="g-3 mb-4">
                {metrics.map((metric) => {
                  const MetricIcon = metric.icon;
                  return (
                    <Col sm={6} lg={4} xl={2} key={metric.key}>
                      <Card className="h-100 p-3 shadow-sm" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div>
                            <div className="text-muted small fw-semibold">{metric.label}</div>
                            <h3 className="mb-0 mt-1 fw-bold" style={{ color: metric.tone }}>{metric.value}</h3>
                          </div>
                          <div
                            className="d-flex align-items-center justify-content-center rounded-3"
                            style={{ width: 42, height: 42, color: metric.tone, backgroundColor: metric.background, flexShrink: 0 }}
                          >
                            <MetricIcon size={19} />
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              <Row className="g-4 pb-4">
                <Col xl={7}>
                  <Card className="p-4 h-100 shadow-sm" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <h5 className="fw-bold mb-1">Top volunteer activity</h5>
                        <div className="small text-muted">Highest approved hours in the selected period.</div>
                      </div>
                      <span className="small fw-semibold px-2 py-1 rounded-pill" style={{ color: "var(--color-primary)", backgroundColor: "#edf2ff" }}>
                        Top 10
                      </span>
                    </div>
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 44 }}>#</th>
                            <th>Student</th>
                            <th>Faculty</th>
                            <th className="text-end">Approved hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.students.slice(0, 10).map((student, index) => (
                            <tr key={`${student.studentNumber}-${student.email}`}>
                              <td className="text-muted">{index + 1}</td>
                              <td className="fw-semibold">{student.name}</td>
                              <td className="text-muted">{student.faculty}</td>
                              <td className="text-end fw-bold" style={{ color: "var(--color-primary)" }}>{student.approvedHours}</td>
                            </tr>
                          ))}
                          {summary.students.length === 0 && (
                            <tr><td colSpan="4" className="text-center text-muted py-4">No volunteer activity matches these filters.</td></tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card>
                </Col>
                <Col xl={5}>
                  <Card className="p-4 h-100 shadow-sm" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                    <div className="mb-3">
                      <h5 className="fw-bold mb-1">Project categories</h5>
                      <div className="small text-muted">Project volume and approved contribution by category.</div>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {summary.distributions.categories.map((category) => {
                        const approvedHours = Number(category.approvedHours) || 0;
                        return (
                          <div key={category.name} className="p-3 rounded-3" style={{ backgroundColor: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                              <div className="fw-semibold">{category.name}</div>
                              <span className="small text-muted text-nowrap">{category.projects} {category.projects === 1 ? "project" : "projects"}</span>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              <ProgressBar
                                now={maxCategoryHours ? (approvedHours / maxCategoryHours) * 100 : 0}
                                style={{ height: 6, flex: 1 }}
                              />
                              <span className="small fw-semibold text-nowrap">{category.approvedHours} hrs</span>
                            </div>
                          </div>
                        );
                      })}
                      {summary.distributions.categories.length === 0 && (
                        <div className="text-center text-muted py-4">No project categories are available.</div>
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default AdminReports;
